import styled from '@emotion/styled';
import { EventTypes } from '@racemap/sdk/schema/events';
import { NULL_TAG } from '@racemap/utilities/consts/events';
import classnames from 'classnames';
import { useCallback, useMemo, useRef, useState } from 'react';
import { unstable_batchedUpdates } from 'react-dom';
import VirtualList, { type ItemStyle } from 'react-tiny-virtual-list';
import type { RenderInfoBuffer } from 'src/lib/player/RenderInfoManager';
import { ALL_TAG } from 'src/lib/player/constants';
import type { EventState, PoiType, StarterType, TrackType } from 'src/lib/player/store';
import { findAllIndexes, matchesTagSelection } from 'src/lib/player/utils';
import { useMeasure } from 'src/lib/player/utils/hooks';
import { onClickGenerator } from 'src/lib/player/utils/onClickGenerator';
import { sleep } from '../utils/Utils';
import SettingsView from './SettingsView';
import ConnectedStarterListItem from './Sidebar/ConnectedStarterListItem';
import EventListItem from './Sidebar/EventListItem';
import { EventsHeader } from './Sidebar/EventsHeader';
import { FavoriteHeader } from './Sidebar/FavoriteHeader';
import { ParticipantsFilter } from './Sidebar/ParticipantsFilter';
import { PoiHeader } from './Sidebar/PoiHeader';
import PoiListItem from './Sidebar/PoiListItem';
import { SidebarSearchForm } from './Sidebar/SidebarSearchForm';
import { StarterGroupHeader } from './Sidebar/StarterGroupHeader';
import { StarterHeader } from './Sidebar/StarterHeader';
import { StarterSortRow } from './Sidebar/StarterSortRow';
import { TrackHeader } from './Sidebar/TrackHeader';
import TrackListItem from './Sidebar/TrackListItem';
import {
  type SidebarItem,
  SidebarItemType,
  SidebarSection,
  type StarterSorting,
} from './Sidebar/types';
import { useSidebarItems } from './Sidebar/useSidebarItems';
import { deduplicateStarters, toggleSelectedTag } from './Sidebar/utils';

const ITEM_HEIGHT = 59;
export const HEADER_HEIGHT = 49;

enum SidebarTab {
  SEARCH = 'SEARCH',
  SETTINGS = 'SETTINGS',
}

export type PlayerSidebarProps = {
  onClose: () => void;
  baseColor?: string;
  allEvents: Array<EventState>;
  currentEventIndices: Array<number>;
  eventType: EventTypes;
  allCurrentStarters: Array<StarterType>;
  allCurrentTracks: Array<TrackType>;
  allCurrentPois: Array<PoiType>;
  currentSelectedIds: Array<string>;
  hiddenIds: Set<string>;
  selectedTags: Map<string, Array<string>>;
  defaultSelectedTags: Map<string, Array<string>>;
  availableTags: Map<string, Array<[string, number]>>;
  availableStarterGroups: Map<string, Array<[string, number]>>;
  renderInfoBuffer: RenderInfoBuffer;
  onSetSelected: (ids: Array<string>) => void;
  onSetSelectedTags: (selectedTags: Map<string, Array<string>>) => void;
  onHide: (ids: Array<string>) => void;
  onUnhide: (ids: Array<string>) => void;
  autoReloadEnabled?: boolean;
  isReloading?: boolean;
  onReload?: () => Promise<void>;
  showSearchAndSettings?: boolean;
  showFavoriteSection?: boolean;
};

export function PlayerSidebar({
  onClose,
  baseColor,
  allEvents,
  currentEventIndices,
  eventType,
  allCurrentStarters,
  allCurrentTracks,
  allCurrentPois,
  currentSelectedIds,
  hiddenIds,
  selectedTags,
  defaultSelectedTags,
  availableTags,
  availableStarterGroups,
  renderInfoBuffer,
  onSetSelected,
  onSetSelectedTags,
  onHide,
  onUnhide,
  autoReloadEnabled,
  isReloading,
  onReload,
  showFavoriteSection = true,
  showSearchAndSettings = true,
}: PlayerSidebarProps) {
  const [listContainerRef, listContainerBounds] = useMeasure();
  const listScrollTopRef = useRef(0);
  const listRef = useRef<VirtualList>(null);

  const showEventSelector =
    (eventType === EventTypes.CONTEST_GROUP || eventType === EventTypes.STAGE_GROUP) &&
    allEvents.length > 0;

  const [searchText, setSearchText] = useState<string>('');
  const [currentTab, setCurrentTab] = useState<SidebarTab>(SidebarTab.SEARCH);
  const [isFilterOpen, setIsFilterOpen] = useState(selectedTags.size > 0);
  const [isSortOpen, setIsSortOpen] = useState(false);
  const [openSections, setOpenSections] = useState(
    showEventSelector
      ? [SidebarSection.EVENTS, SidebarSection.STARTERS, SidebarSection.FAVORITE]
      : [SidebarSection.STARTERS, SidebarSection.FAVORITE],
  );
  const [openStarterGroups, setOpenStarterGroups] = useState<Array<string>>([]);
  const [starterSorting, setStarterSorting] = useState<StarterSorting | null>(null);

  const filteredStarters = useMemo(() => {
    return selectedTags.size === 0
      ? allCurrentStarters
      : allCurrentStarters.filter((starter) => matchesTagSelection(starter.tags, selectedTags));
  }, [allCurrentStarters, selectedTags]);

  const deduplicatedStarters = useMemo(
    () => deduplicateStarters(filteredStarters),
    [filteredStarters],
  );

  const onToggleSection = useCallback(
    (section: SidebarSection) => {
      if (openSections.includes(section)) {
        setOpenSections(openSections.filter((s) => s !== section));
      } else {
        setOpenSections([section, ...openSections]);
      }
    },
    [openSections],
  );
  const onToggleStarterGroup = useCallback(
    (tagKey: string, tagValue: string) => {
      const key = `${tagKey}-${tagValue}`;
      if (openStarterGroups.includes(key)) {
        setOpenStarterGroups(openStarterGroups.filter((s) => s !== key));
      } else {
        setOpenStarterGroups([...openStarterGroups, key]);
      }
    },
    [openStarterGroups],
  );

  function calculateSectionOffset(sectionLabel: SidebarSection): number {
    let offset = 0;
    for (const item of items) {
      if (item.type === SidebarItemType.HEADER && item.label === sectionLabel) {
        return offset;
      }
      switch (item.type) {
        case SidebarItemType.HEADER:
          offset += HEADER_HEIGHT;
          break;
        case SidebarItemType.STARTER_GROUP:
          offset += HEADER_HEIGHT;
          break;
        default:
          offset += ITEM_HEIGHT;
          break;
      }
    }
    return 0;
  }

  function switchToSearchTab() {
    unstable_batchedUpdates(() => {
      if (currentTab !== SidebarTab.SEARCH) {
        setCurrentTab(SidebarTab.SEARCH);
      }
    });
  }

  function toggleSettingsTab() {
    setCurrentTab((currentTag) =>
      currentTag === SidebarTab.SETTINGS ? SidebarTab.SEARCH : SidebarTab.SETTINGS,
    );
  }

  function scrollCorrection(_: unknown, isSelected: boolean) {
    if (openSections.includes(SidebarSection.FAVORITE)) {
      if (listRef.current == null) return;

      const newScrollTop = isSelected
        ? listScrollTopRef.current + ITEM_HEIGHT
        : listScrollTopRef.current - ITEM_HEIGHT;

      // @ts-expect-error - workaround to access internal scroll container of VirtualList
      listRef.current.rootNode.scrollTo({
        top: newScrollTop,
        behavior: 'auto',
      });
      listScrollTopRef.current = newScrollTop;
    }
  }

  function handleToggleSort() {
    const sortBoxOffset = calculateSectionOffset(SidebarSection.STARTERS) + HEADER_HEIGHT;

    const sortBoxIsInView =
      listScrollTopRef.current + HEADER_HEIGHT <= sortBoxOffset &&
      // @ts-expect-error - workaround to access internal scroll container of VirtualList
      sortBoxOffset <= listScrollTopRef.current + (listRef.current?.rootNode.clientHeight || 0);

    if (isSortOpen && sortBoxIsInView) {
      setIsSortOpen(false);
    } else {
      setIsSortOpen(true);
      setIsFilterOpen(false);
      if (!sortBoxIsInView) {
        // @ts-expect-error - workaround to access internal scroll container of VirtualList
        listRef.current?.rootNode.scrollTo({
          top: sortBoxOffset - HEADER_HEIGHT,
          behavior: 'smooth',
        });
        listScrollTopRef.current = sortBoxOffset - HEADER_HEIGHT;
      }
    }
  }

  function handleToggleFilter() {
    const filterBoxOffset = calculateSectionOffset(SidebarSection.STARTERS) + HEADER_HEIGHT;

    const filterBoxIsInView =
      listScrollTopRef.current + HEADER_HEIGHT <= filterBoxOffset &&
      // @ts-expect-error - workaround to access internal scroll container of VirtualList
      filterBoxOffset <= listScrollTopRef.current + (listRef.current?.rootNode.clientHeight || 0);

    if (isFilterOpen && filterBoxIsInView) {
      setIsFilterOpen(false);
    } else {
      setIsFilterOpen(true);
      setIsSortOpen(false);
      if (!filterBoxIsInView) {
        // @ts-expect-error - workaround to access internal scroll container of VirtualList
        listRef.current?.rootNode.scrollTo({
          top: filterBoxOffset - HEADER_HEIGHT,
          behavior: 'smooth',
        });
        listScrollTopRef.current = filterBoxOffset - HEADER_HEIGHT;
      }
    }
  }

  function renderEventRow(event: EventState, style: ItemStyle) {
    return (
      <div key={event.eventId} style={style}>
        <EventListItem event={event} />
      </div>
    );
  }

  function renderStarterRow(
    starter: StarterType,
    style: ItemStyle,
    isFavorite = false,
    isGroup: string | null = null,
  ) {
    const renderIndex = renderInfoBuffer.getRenderIndex(starter.eventIndex, starter.index);
    return (
      <div key={`${starter.id}-${isFavorite}-${isGroup}`} style={style}>
        <ConnectedStarterListItem
          starter={starter}
          starterOpacity={
            currentEventIndices.includes(starter.eventIndex) && !hiddenIds.has(starter.id)
              ? renderInfoBuffer.getOpacity(renderIndex)
              : 0
          }
          starterOffset={renderInfoBuffer.getOffset(renderIndex)}
          starterTimestamp={renderInfoBuffer.getTimestamp(renderIndex)}
          onClick={scrollCorrection}
        />
      </div>
    );
  }

  function renderPoiRow(poi: PoiType, style: ItemStyle, isFavorite = false) {
    return (
      <div key={`poi-${poi.id}-${isFavorite}`} style={style}>
        <PoiListItem poi={poi} onClick={scrollCorrection} />
      </div>
    );
  }

  function renderTrackRow(track: TrackType, style: ItemStyle, isFavorite = false) {
    return (
      <div key={`track-${track.id}-${isFavorite}`} style={style}>
        <TrackListItem track={track} onClick={scrollCorrection} />
      </div>
    );
  }

  const items = useSidebarItems({
    searchText,
    openSections,
    openStarterGroups,
    isFilterOpen,
    isSortOpen,
    starterSorting,
    renderInfoBuffer,
    showEventSelector,
    eventType,
    allEvents,
    allCurrentStarters,
    allCurrentTracks,
    allCurrentPois,
    currentSelectedIds,
    availableTags,
    availableStarterGroups,
    filteredStarters,
    deduplicatedStarters,
    showFavoriteSection,
  });

  const stickyIndices = findAllIndexes(
    items,
    (item: SidebarItem) => item.type === SidebarItemType.HEADER,
  );

  return (
    <SidebarRoot className="side-bar" baseColor={baseColor}>
      {showSearchAndSettings && (
        <SidebarSearchForm
          searchText={searchText}
          isSettingsTabActive={currentTab === SidebarTab.SETTINGS}
          onSearchTextChange={setSearchText}
          onClose={onClose}
          onSwitchToSearchTab={switchToSearchTab}
          onToggleSettingsTab={toggleSettingsTab}
          autoReloadEnabled={autoReloadEnabled}
          isReloading={isReloading}
          onReload={onReload}
        />
      )}
      <div ref={listContainerRef} style={{ flex: 1, overflow: 'hidden', position: 'relative' }}>
        {currentTab === SidebarTab.SEARCH ? (
          <VirtualList
            ref={listRef}
            width="100%"
            height={listContainerBounds.height}
            style={{ flex: '1' }}
            className="starter-list"
            itemCount={items.length}
            itemSize={(index: number) => {
              const item = items[index];
              switch (item.type) {
                case SidebarItemType.HEADER:
                  return HEADER_HEIGHT;
                case SidebarItemType.STARTER_GROUP:
                  return HEADER_HEIGHT;
                case SidebarItemType.STARTER_FILTER:
                  return 40 + item.itemCount * 40 + 1;
                case SidebarItemType.STARTER_SORT:
                  return 40 + 3 * 40 + 1;
                default:
                  return ITEM_HEIGHT;
              }
            }}
            stickyIndices={stickyIndices}
            overscanCount={20}
            estimatedItemSize={ITEM_HEIGHT}
            onScroll={(scrollOffset: number) => {
              listScrollTopRef.current = scrollOffset;
            }}
            renderItem={({ index, style }: { index: number; style: ItemStyle }) => {
              const item = items[index];
              switch (item.type) {
                case SidebarItemType.HEADER:
                  switch (item.label) {
                    case SidebarSection.FAVORITE: {
                      const anySelected = currentSelectedIds.length > 0;
                      return (
                        <FavoriteHeader
                          key="header-FAVORITE"
                          style={style}
                          isOpen={openSections.includes(SidebarSection.FAVORITE) && anySelected}
                          anySelected={anySelected}
                          allHidden={
                            anySelected && currentSelectedIds.every((id) => hiddenIds.has(id))
                          }
                          onToggle={() => onToggleSection(SidebarSection.FAVORITE)}
                          onClearSelection={() => onSetSelected([])}
                          onHide={() => onHide(currentSelectedIds)}
                          onUnhide={() => onUnhide(currentSelectedIds)}
                        />
                      );
                    }
                    case SidebarSection.TRACK: {
                      const trackIds = allCurrentTracks.map((track) => track.id);
                      return (
                        <TrackHeader
                          key="header-TRACK"
                          style={style}
                          isOpen={openSections.includes(SidebarSection.TRACK)}
                          allHidden={
                            trackIds.length > 0 && trackIds.every((id) => hiddenIds.has(id))
                          }
                          onToggle={() => onToggleSection(SidebarSection.TRACK)}
                          onHide={() => onHide(trackIds)}
                          onUnhide={() => onUnhide(trackIds)}
                        />
                      );
                    }
                    case SidebarSection.POI: {
                      const poiIds = allCurrentPois.map((poi) => poi.id);
                      return (
                        <PoiHeader
                          key="header-POI"
                          style={style}
                          isOpen={openSections.includes(SidebarSection.POI)}
                          allHidden={poiIds.length > 0 && poiIds.every((id) => hiddenIds.has(id))}
                          onToggle={() => onToggleSection(SidebarSection.POI)}
                          onHide={() => onHide(poiIds)}
                          onUnhide={() => onUnhide(poiIds)}
                        />
                      );
                    }
                    case SidebarSection.STARTERS: {
                      const starterIds = allCurrentStarters.map((s) => s.id);
                      return (
                        <StarterHeader
                          key="header-STARTERS"
                          style={style}
                          isOpen={openSections.includes(SidebarSection.STARTERS)}
                          filteredCount={filteredStarters.length}
                          totalCount={allCurrentStarters.length}
                          hasSelectedTags={selectedTags.size > 0}
                          isSortOpen={isSortOpen}
                          isFilterOpen={isFilterOpen}
                          hasAvailableTags={availableTags.size > 0}
                          allHidden={
                            starterIds.length > 0 && starterIds.every((id) => hiddenIds.has(id))
                          }
                          onToggle={() => onToggleSection(SidebarSection.STARTERS)}
                          onToggleSort={handleToggleSort}
                          onToggleFilter={handleToggleFilter}
                          onHide={() => onHide(starterIds)}
                          onUnhide={() => onUnhide(starterIds)}
                        />
                      );
                    }
                    case SidebarSection.EVENTS:
                      return (
                        <EventsHeader
                          key="header-EVENT"
                          style={style}
                          isOpen={openSections.includes(SidebarSection.EVENTS)}
                          eventType={item.eventType}
                          onToggle={() => onToggleSection(SidebarSection.EVENTS)}
                        />
                      );
                    default:
                      return null;
                  }
                case SidebarItemType.EVENT:
                  return renderEventRow(item.event, style);
                case SidebarItemType.STARTER:
                  return renderStarterRow(item.starter, style, item.isFavorite, item.group);
                case SidebarItemType.STARTER_FILTER:
                  return (
                    <ParticipantsFilter
                      availableTags={availableTags}
                      itemStyle={style}
                      selectedTags={selectedTags}
                      onToggleTag={(tagKey, value) => {
                        onSetSelectedTags(toggleSelectedTag(selectedTags, tagKey, value));
                      }}
                      onReset={async () => {
                        onSetSelectedTags(defaultSelectedTags);
                        await sleep(300);
                        setIsFilterOpen(false);
                      }}
                      defaultSelectedTags={defaultSelectedTags}
                    />
                  );
                case SidebarItemType.STARTER_SORT:
                  return (
                    <StarterSortRow
                      key="starter-sort-row"
                      style={style}
                      starterSorting={starterSorting}
                      onSort={(sortKey, direction) => setStarterSorting([sortKey, direction])}
                      onReset={() => {
                        setStarterSorting(null);
                        setIsSortOpen(false);
                      }}
                    />
                  );
                case SidebarItemType.STARTER_GROUP: {
                  const startersInGroup =
                    item.tagKey === ALL_TAG
                      ? deduplicatedStarters
                      : deduplicatedStarters.filter(
                          (starter) =>
                            starter.tags.get(item.tagKey) === item.tagValue ||
                            (item.tagValue === NULL_TAG && !starter.tags.has(item.tagKey)),
                        );
                  const groupIds = startersInGroup.map((s) => s.id);
                  return (
                    <StarterGroupHeader
                      key={`header-STARTER-GROUP-${item.tagKey}-${item.tagValue}`}
                      style={style}
                      tagKey={item.tagKey}
                      tagValue={item.tagValue}
                      isOpen={openStarterGroups.includes(`${item.tagKey}-${item.tagValue}`)}
                      starterCount={startersInGroup.length}
                      allHidden={groupIds.length > 0 && groupIds.every((id) => hiddenIds.has(id))}
                      onToggle={() => onToggleStarterGroup(item.tagKey, item.tagValue)}
                      onHide={() => onHide(groupIds)}
                      onUnhide={() => onUnhide(groupIds)}
                    />
                  );
                }
                case SidebarItemType.POI:
                  return renderPoiRow(item.poi, style, item.isFavorite);
                case SidebarItemType.TRACK:
                  return renderTrackRow(item.track, style, item.isFavorite);
                default:
                  return null;
              }
            }}
          />
        ) : (
          <SettingsView />
        )}
      </div>
    </SidebarRoot>
  );
}

export function SidebarButton({
  isOpen,
  onToggle,
}: {
  isOpen: boolean;
  onToggle: () => void;
}): JSX.Element {
  return (
    <SearchButtonContainer className={classnames('search-button-container', { 'is-open': isOpen })}>
      <div
        className="search-button"
        {...onClickGenerator(() => {
          onToggle();
        })}
      >
        <i className="fas fa-search" />
      </div>
    </SearchButtonContainer>
  );
}

const SidebarRoot = styled.div<{ baseColor?: string }>`
  ${({ baseColor }) =>
    baseColor != null
      ? `
    --base-color: ${baseColor};
    --base-color-15: color-mix(in srgb, var(--base-color) 15%, transparent);
    --base-color-30: color-mix(in srgb, var(--base-color) 30%, transparent);
    --base-color-50: color-mix(in srgb, var(--base-color) 50%, transparent);
    --base-color-dark: color-mix(in srgb, var(--base-color), black 10%);
    --base-color-light: color-mix(in hsl, var(--base-color) 20%, white);
  `
      : ''}

  --player-text-color: var(--text-color, #444444);
  --player-color-light-grey: var(--color-light-grey, #dddddd);
  --player-color-mid-grey: var(--color-mid-grey, #aaaaaa);
  --player-color-dark-grey: var(--color-dark-grey, #888888);

  display: flex;
  flex-direction: column;
  height: 100%;

  .side-bar-search {
    display: flex;

    form.search-form {
      border-radius: 4px;
      border: 1px solid var(--base-color-dark);
      background: white;
      width: auto;
      margin: 10px;
      position: relative;
      flex: 1;

      input {
        height: 40px;
        line-height: 16px;
        box-shadow: none;
        padding-left: 38px;
        padding-top: 12px;
        padding-bottom: 12px;
        border-radius: 4px;
        width: 100%;
        border: none;
        outline: none;
      }

      .search-icon {
        position: absolute;
        top: 0;
        left: 0;
        padding: 12px;
        font-size: 16px;
        cursor: pointer;
        transition: transform 0.3s;
      }

      .settings-icon {
        position: absolute;
        top: 0;
        right: -40px;
        padding: 12px;
        font-size: 16px;
        cursor: pointer;

        &.settings-icon-open {
          color: var(--base-color);
        }
      }

      .clear-icon {
        position: absolute;
        top: 0;
        right: 0;
        padding: 12px;
        font-size: 16px;
        cursor: pointer;
      }

      .close-icon {
        display: none;
        position: absolute;
        top: 0;
        left: -40px;
        padding: 12px;
        font-size: 16px;
        cursor: pointer;
      }

      @media screen and (max-width: 750px) {
        margin-left: 40px;

        .close-icon {
          display: block;
        }
      }
    }
  }

  .settings-form {
    background: rgba(255, 255, 255, 0.87);
    min-height: 100%;
    border-top: 1px solid var(--base-color-30);

    > div {
      display: flex;
      padding: 10px;
    }

    span,
    select,
    input {
      flex: 1;
    }
  }

  .close-button {
    height: 30px;
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
  }

  .tag-multiselect {
    display: flex;
    padding: 5px 10px;
    align-items: start;

    .tag-multiselect-key {
      line-height: 30px;
      font-weight: 600;
      width: 100px;
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
    }

    .tag-multiselect-values {
      position: relative;
      flex: 1;
      margin-left: 5px;
      min-height: 30px;

      .tag-multiselect-input {
        position: absolute;
        left: 0;
        top: 0;
        width: 100%;
        height: 30px;
        padding: 5px;
        flex: 1;
        display: flex;
        justify-content: space-between;
        align-items: center;
        overflow: hidden;
        white-space: nowrap;
        border: 1px solid var(--base-color-dark);
        border-radius: 4px;
        background-color: white;

        .tag-multiselect-item {
          display: inline-block;
          padding-right: 5px;

          &.preview {
            color: lightgrey;
          }

          &.selected {
            border-radius: 3px;
            background-color: var(--base-color-30);
            padding: 3px 6px;
            margin-right: 5px;
          }
        }
      }

      .tag-multiselect-scroller {
        position: absolute;
        top: 26px;
        left: 0;
        width: 100%;
        min-height: 30px;
        max-height: 200px;
        overflow-y: auto;
        border: 1px solid var(--base-color-50);
        border-radius: 0 0 4px 4px;
        border-top: none;
        padding-top: 4px;
        z-index: 10;
        background-color: white;

        .tag-multiselect-item {
          line-height: 30px;
          padding: 0 5px;
          height: 30px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;

          &.selected {
            background-color: var(--base-color-30);
          }

          .tag-multiselect-item-count {
            color: #aaaaaa;
          }
        }
      }

      i.fa-times {
        position: absolute;
        right: 20px;
        top: 0;
        z-index: 10;
        padding: 9px;
        padding-right: 5px;
        background-color: white;
      }

      i.fa-caret-up,
      i.fa-caret-down {
        width: 16px;
        position: absolute;
        right: 9px;
        top: 0;
        z-index: 10;
        padding: 9px;
        padding-right: 5px;
        background-color: white;
      }
    }
  }

  .sort-select {
    display: flex;
    padding: 5px 10px;
    align-items: start;

    .sort-select-key {
      line-height: 30px;
      font-weight: 600;
      flex: 1;
    }

    .sort-select-values {
      display: flex;
      flex-direction: row;
      margin-left: 5px;
      min-height: 30px;

      .sort-select-value {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 30px;
        height: 30px;
        font-size: 20px;
        cursor: pointer;
        color: #888888;

        &.active {
          color: var(--base-color);
        }
      }
    }
  }

  .side-bar-toolbar-button {
    display: flex;
    height: 42px;
    width: 42px;
    justify-content: center;
    align-items: center;
    border: 1px solid var(--base-color-50);
    cursor: pointer;
    font-weight: 600;
    margin: 10px;
    margin-left: 0;
    border-radius: 4px;
    text-align: center;

    &.active {
      color: var(--base-color);
      border-width: 2px;
    }

    &.disabled {
      color: var(--player-color-mid-grey);
      border-color: var(--player-color-mid-grey);
      cursor: inherit;
    }

    &:not(.disabled):hover {
      background-color: var(--base-color-30);
    }
  }

  .starter-list {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    list-style: none;
    padding: 0;
    margin: 0;
    -webkit-overflow-scrolling: touch;
    overflow-y: auto;
    border-top: 1px solid var(--base-color-30);
  }

  .starter-list-header {
    display: flex;
    flex-direction: row;
    background-color: white;
    border-bottom: 1px solid white;

    &.open {
      border-bottom: 1px solid var(--base-color-30);
    }
  }

  .starter-list-group-header .starter-list-header-item {
    background-color: var(--base-color-15);
  }

  .starter-list-header-item {
    flex: 1;
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: var(--base-color-30);
    padding: 0 10px;

    .starter-list-header-item-label {
      font-weight: 600;
      flex: 1;
    }

    .starter-list-header-icon-button {
      cursor: pointer;
      font-size: 15px;
      height: 30px;
      width: 30px;
      display: flex;
      align-items: center;
      justify-content: center;

      &.active {
        color: var(--base-color);
      }
    }

    .starter-list-header-item-button {
      border: 1px solid var(--base-color-50);
      padding: 5px 10px;
      border-radius: 4px;
      cursor: pointer;
      font-size: 0.9em;
      height: 26px;
      display: flex;
      align-items: center;
      justify-content: center;

      &.active {
        border-width: 2px;
      }

      &:hover {
        background-color: var(--base-color-30);
      }
    }

    .starter-list-header-item-button + .starter-list-header-item-button {
      margin-left: 10px;
    }

    &.starter-list-header-item-selected {
      color: var(--base-color);
    }

    .starter-list-header-item-info {
      display: inline-block;
      margin-left: 5px;
      color: var(--base-color-50);
      font-weight: normal;
      font-size: 0.9em;
    }
  }

  .starter-list-button {
    display: flex;
    height: 30px;
    justify-content: center;
    align-items: center;
    border: 1px solid var(--base-color-50);
    padding: 5px 10px;
    border-radius: 4px;
    cursor: pointer;
    font-size: 0.9em;
    text-align: center;
    background-color: white;
    margin: 5px 10px;

    &.starter-list-button-disabled {
      color: var(--player-color-light-grey);
      border-color: var(--player-color-light-grey);
      cursor: unset;
      pointer-events: none;
      
      &:hover {
        background-color: white;
      }
    }

    &.active {
      border-width: 2px;
    }

    &:hover {
      background-color: var(--base-color-30);
    }
  }

  .starter-list-filter {
    border-bottom: 1px solid var(--player-color-light-grey);
  }

  .starter-list-item {
    display: flex;
    position: relative;
    height: 59px;
    padding: 5px 10px 5px 17px;
    flex-direction: row;
    align-items: center;
    line-height: 1.2em;
    cursor: pointer;
    overflow: hidden;
    border-bottom: 1px solid var(--player-color-light-grey);
    text-overflow: ellipsis;
    white-space: nowrap;
    background: rgba(255, 255, 255, 0.87);
    user-select: text;
    -webkit-user-select: text;

    &:hover {
      background: var(--base-color-light);
    }

    > div {
      display: flex;
      align-items: center;
      overflow: hidden;
      text-overflow: ellipsis;
      width: 100%;
      vertical-align: middle;
    }

    .starter-text {
      display: flex;
      flex-direction: column;
      flex: 1;
    }

    .starter-name {
      font-weight: 600;
      display: flex;
      align-items: center;
      flex-direction: row;
      width: 100%;

      .starter-name-content {
        flex: 1;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        margin-right: 5px;

        @supports (-webkit-line-clamp: 2) {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          white-space: normal;
        }
      }
    }

    .starter-info {
      margin-top: 3px;
      font-size: 0.9em;
      overflow: hidden;
      text-overflow: ellipsis;
      max-width: 100%;
      white-space: nowrap;

      span {
        margin-right: 10px;
      }
    }

    .starter-color {
      position: absolute;
      width: 7px;
      height: 100%;
      left: 0;
      top: 0;
      border-right: 1px solid var(--player-color-light-grey);
    }

    .starter-controls {
      display: flex;
      align-items: center;
      justify-content: center;

      i {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 30px;
        height: 30px;
        font-size: 15px;
        cursor: pointer;
        color: var(--player-color-dark-grey);

        &:hover {
          color: var(--player-text-color);
        }

        &.fas.fa-star {
          color: var(--base-color);
        }
      }
    }
  }
`;

const SearchButtonContainer = styled.div`
  pointer-events: all;
  position: absolute;
  top: 10px;
  left: 10px;
  z-index: 100;

  .search-button {
    border-radius: 4px;
    border: 1px solid var(--base-color-dark);
    background: white;
    cursor: pointer;
    padding: 12px;
    font-size: 16px;
    margin: 10px;
    width: 50px;
    height: 50px;
    border-radius: 25px;
    transition: background-color 0.2s ease, color 0.2s ease;

    i {
      transform: translateX(4px) translateY(3px);
    }
  }

  @media screen and (min-width: 750px) {
    &.is-open .search-button {
      background-color: var(--base-color);
      color: white;
    }
  }
`;
